import adahAvatar from '@/assets/images/adah-ojile-avatar.png';
import type { BlogPost, BlogVariant, NewsItem } from '@/types/blog';
import { getBlogPostHref } from '@/utils/blog-config';

const ENTITY_MAP: Record<string, string> = {
  '&amp;': '&',
  '&nbsp;': ' ',
  '&quot;': '"',
  '&#39;': "'",
  '&rsquo;': "'",
  '&lsquo;': "'",
  '&rdquo;': '"',
  '&ldquo;': '"',
  '&mdash;': '-',
  '&ndash;': '-',
};

const toText = (value: unknown): string => {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.map(toText).filter(Boolean).join(', ');
  }
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const preferredKeys = [
      'rendered',
      'name',
      'title',
      'label',
      'slug',
      'value',
    ];

    for (const key of preferredKeys) {
      const text = toText(record[key]);
      if (text) return text;
    }
  }

  return '';
};

export const decodeHtmlEntities = (value: unknown) =>
  toText(value)
    .replace(
      /&(?:amp|nbsp|quot|#39|rsquo|lsquo|rdquo|ldquo|mdash|ndash);/g,
      match => ENTITY_MAP[match] ?? match
    )
    .replace(/&#(\d+);/g, (match, codePoint: string) => {
      const parsed = Number(codePoint);
      return Number.isFinite(parsed) ? String.fromCodePoint(parsed) : match;
    })
    .replace(/&#x([0-9a-f]+);/gi, (match, codePoint: string) => {
      const parsed = Number.parseInt(codePoint, 16);
      return Number.isFinite(parsed) ? String.fromCodePoint(parsed) : match;
    });

const removeWordPressChrome = (value: string) =>
  value
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<div[^>]*class=(["'])[^"']*gallery[^"']*\1[\s\S]*?<\/div>/gi, '')
    .replace(/<br[^>]*style=(["'])[^"']*clear:\s*both[^"']*\1[^>]*>/gi, '');

export const stripHtml = (value?: unknown) =>
  removeWordPressChrome(decodeHtmlEntities(value))
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export const htmlToMarkdown = (value?: unknown) =>
  removeWordPressChrome(decodeHtmlEntities(value))
    .replace(/<h1[^>]*>([\s\S]*?)<\/h1>/gi, '# $1\n\n')
    .replace(/<h2[^>]*>([\s\S]*?)<\/h2>/gi, '## $1\n\n')
    .replace(/<h3[^>]*>([\s\S]*?)<\/h3>/gi, '### $1\n\n')
    .replace(/<p[^>]*>([\s\S]*?)<\/p>/gi, '$1\n\n')
    .replace(/<li[^>]*>([\s\S]*?)<\/li>/gi, '- $1\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/?(ul|ol)[^>]*>/gi, '\n')
    .replace(/<strong[^>]*>([\s\S]*?)<\/strong>/gi, '**$1**')
    .replace(/<b[^>]*>([\s\S]*?)<\/b>/gi, '**$1**')
    .replace(/<em[^>]*>([\s\S]*?)<\/em>/gi, '*$1*')
    .replace(/<i[^>]*>([\s\S]*?)<\/i>/gi, '*$1*')
    .replace(/<a[^>]*href="([^"]+)"[^>]*>([\s\S]*?)<\/a>/gi, '[$2]($1)')
    .replace(/<[^>]+>/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

const getPostTags = (post: BlogPost, limit?: number) => {
  const tags = [
    ...Object.values(post.tags ?? {}),
    ...Object.values(post.terms?.post_tag ?? {}),
  ]
    .map(stripHtml)
    .filter(Boolean);

  const uniqueTags = Array.from(new Set(tags));
  return typeof limit === 'number' ? uniqueTags.slice(0, limit) : uniqueTags;
};

const getPostImage = (post: BlogPost, variant: BlogVariant) => {
  if (post.featured_image) return post.featured_image;
  if (post.post_thumbnail) return post.post_thumbnail;

  const attachment = Object.values(post.attachments ?? {}).find(
    item => item.URL && (!item.mime_type || item.mime_type.startsWith('image/'))
  );

  if (attachment?.thumbnails?.large) return attachment.thumbnails.large;
  if (attachment?.thumbnails?.['newspack-article-block-landscape-large']) {
    return attachment.thumbnails['newspack-article-block-landscape-large'];
  }
  if (attachment?.URL) return attachment.URL;

  return variant === 'inference'
    ? '/blog-inference-bg.png'
    : '/blog-ledger-bg.png';
};

export const mapBlogPostToNewsItem = (
  post: BlogPost,
  variant: BlogVariant
): NewsItem => ({
  author:
    stripHtml(post.author?.name) ||
    stripHtml(
      [post.author?.first_name, post.author?.last_name]
        .filter(Boolean)
        .join(' ')
    ) ||
    'Mariana',
  avatar: post.author?.avatar_URL || adahAvatar,
  timestamp: post.date || '',
  tags: getPostTags(post, 3),
  title: stripHtml(post.title),
  description: stripHtml(post.excerpt || post.content),
  image: getPostImage(post, variant),
  href: getBlogPostHref(post.ID, variant, post.slug || stripHtml(post.title)),
});

export const mapBlogPostToArticle = (post: BlogPost, variant: BlogVariant) => ({
  id: post.ID,
  slug: post.slug || String(post.ID),
  title: stripHtml(post.title),
  subtitle: stripHtml(post.excerpt),
  author:
    stripHtml(post.author?.name) ||
    stripHtml(
      [post.author?.first_name, post.author?.last_name]
        .filter(Boolean)
        .join(' ')
    ) ||
    'Mariana',
  authorAvatar: post.author?.avatar_URL || adahAvatar,
  timestamp: post.date || '',
  publishedTime: post.date,
  image: getPostImage(post, variant),
  tags: getPostTags(post),
  content: htmlToMarkdown(post.content || post.excerpt),
});
